import _ from 'lodash';
import React from 'react';
import {default as Modal, withModal} from 'components/Modal';
import Button from 'components/Button';
import Avatar from 'components/Avatar';
import Warning from 'components/Warning';
import {ResendInvitationForm, ResetPasswordForm} from './components';
import {
  MODALS,
  fetchStaffMember,
  promoteTeamOwner,
  demoteTeamOwner,
  activateUser,
  deactivateUser,
  removeMemberFromTeam,
  fetchStaffMembers,
  fetchAllTeams,
  fetchMyTeams
} from 'ducks/teams';
import {requiresPermission, PERMISSIONS} from 'ducks/auth';
import {ButtonDropdown} from 'containers/MyTeams/components/shared';
import {
  default as MemberStatus,
  MEMBER_STATUSES
} from 'containers/MyTeams/components/shared/MemberStatus';

import './styles.css';
import {formatDateTime, formatDate} from 'utils/datetime';

// A single invitation renders two invitation rows
const InvitationRow = ({invitation}) => (
  <React.Fragment>
    {invitation.createdAt && (
      <div className="row">
        <div className="info">
          <div>Invitation sent</div>
          <div className="light">Sent by an Admin</div>
        </div>
        <div className="date">{formatDate(invitation.createdAt)}</div>
      </div>
    )}
    {invitation.accepted && (
      <div className="row">
        <div className="info">
          <div>Invitation accepted</div>
          <div className="light">Accepted</div>
        </div>
        <div className="date">{formatDate(invitation.acceptedAt)}</div>
      </div>
    )}
  </React.Fragment>
);

class StaffMemberDetailModal extends React.Component<any> {
  componentDidMount() {
    this.fetchData();
  }

  getFetchDataExtraActions = () => [
    ...this.props.storeDataExtraActions(data => ({member: data})),
    fetchStaffMembers,
    fetchAllTeams,
    fetchMyTeams
  ];

  fetchData = () => {
    const {memberId} = this.props.data;
    return this.props.fetchStaffMember(
      {memberId},
      this.getFetchDataExtraActions()
    );
  };

  promoteTeamOwner = teamId => {
    const {member} = this.props.data;
    this.props.promoteTeamOwner({teamId, memberId: member.id}, [
      () => this.fetchData()
    ]);
  };

  demoteTeamOwner = teamId => {
    const {member} = this.props.data;
    this.props.demoteTeamOwner({teamId, memberId: member.id}, [
      () => this.fetchData()
    ]);
  };

  removeFromTeam = teamId => {
    const {member} = this.props.data;

    this.props.removeMemberFromTeam({memberId: member.id, teamId}, [
      () =>
        fetchStaffMember({memberId: member.id}, this.getFetchDataExtraActions())
    ]);
  };

  activateUser = () => {
    const {member} = this.props.data;

    this.props.requiresPermission(
      {permission: PERMISSIONS.EDIT_STAFF_MEMBER_DETAILS},
      () =>
        this.props.activateUser({userId: member.id}, [
          () =>
            fetchStaffMember(
              {memberId: member.id},
              this.getFetchDataExtraActions()
            )
        ])
    );
  };

  deactivateUser = () => {
    const {member} = this.props.data;

    this.props.requiresPermission(
      {permission: PERMISSIONS.EDIT_STAFF_MEMBER_DETAILS},
      () =>
        this.props.deactivateUser({userId: member.id}, [
          () =>
            fetchStaffMember(
              {memberId: member.id},
              this.getFetchDataExtraActions()
            )
        ])
    );
  };

  render() {
    const member = this.props.data.member;
    const invitations = _.get(member, 'invitations', []);
    const invitationsExists = !_.isEmpty(invitations);

    return (
      <Modal
        className="StaffMemberDetailModal"
        type={Modal.EXTRA_SMALL}
        show={true}
        onClose={this.props.closeModal}>
        <Modal.Header heading="Staff Member Details" />
        {member && (
          <Modal.Body>
            <div className="content">
              <div className="memberDetails">
                <div className="info">
                  <Avatar
                    url={member.internalAvatarCropped || member.avatarCropped}
                    initials={member.initials}
                  />
                  <div className="info">
                    <div className="name">{member.name}</div>
                    <div className="status">
                      <MemberStatus status={member.status} />
                      <div className="lastSignedIn">
                        Last signed in:{' '}
                        {member.lastLogin
                          ? formatDateTime(member.lastLogin)
                          : ' Never'}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="actions">
                  {member.isActive && (
                    <ButtonDropdown
                      title="Active"
                      dropdownClassName="StaffMemberDetailStatusDropdown"
                      menuPosition={ButtonDropdown.MENU_POSITION_BOTTOM}>
                      <ButtonDropdown.Option
                        onClick={() => this.deactivateUser()}
                        disabled={!member.canDeactivate}>
                        Deactivate
                      </ButtonDropdown.Option>
                    </ButtonDropdown>
                  )}
                  {!member.isActive && (
                    <ButtonDropdown title="Deactivated">
                      <ButtonDropdown.Option
                        onClick={() => this.activateUser()}
                        disabled={!member.canActivate}>
                        Activate
                      </ButtonDropdown.Option>
                    </ButtonDropdown>
                  )}
                </div>
              </div>

              <div className="teamsSection">
                <div className="heading">Teams</div>
                {!_.isEmpty(member.teams) && (
                  <div className="content">
                    {member.teams.map(team => (
                      <div className="teamRow" key={team.id}>
                        <div className="info">
                          <Avatar url={team.avatar} initials="" />
                          <div className="info">
                            <div className="name">{team.name}</div>
                            {team.isOwner && (
                              <div className="info">Team owner</div>
                            )}
                            {!team.isOwner && (
                              <div className="info">Team member</div>
                            )}
                          </div>
                        </div>
                        <ButtonDropdown
                          className="modded"
                          title="Edit"
                          menuPosition={
                            ButtonDropdown.MENU_POSITION_BOTTOM_RIGHT
                          }>
                          {team.isOwner && (
                            <ButtonDropdown.Option
                              onClick={() => this.demoteTeamOwner(team.id)}>
                              Demote to team member
                            </ButtonDropdown.Option>
                          )}
                          {!team.isOwner && (
                            <ButtonDropdown.Option
                              onClick={() => this.promoteTeamOwner(team.id)}>
                              Promote to team owner
                            </ButtonDropdown.Option>
                          )}
                          <ButtonDropdown.Option
                            className="red"
                            onClick={() => this.removeFromTeam(team.id)}>
                            Remove from team
                          </ButtonDropdown.Option>
                        </ButtonDropdown>
                      </div>
                    ))}
                  </div>
                )}
                {_.isEmpty(member.teams) && (
                  <div className="content noTeams">No teams so far</div>
                )}
              </div>
              <div className="colabInvitation">
                <div className="heading">Colab Invitation</div>
                {member.status === MEMBER_STATUSES.INVITE_EXPIRED && (
                  <Warning>The invitation has expired.</Warning>
                )}
                <div className="content">
                  {invitationsExists &&
                    invitations.map(invitation => (
                      <InvitationRow
                        invitation={invitation}
                        key={invitation.id}
                      />
                    ))}

                  {!invitationsExists && (
                    <div className="row">No current invitations</div>
                  )}
                </div>
                <div className="invitationButtons">
                  <ResendInvitationForm
                    member={member}
                    getFetchDataExtraActions={this.getFetchDataExtraActions}
                    disableButton={!member.canResendInvitation}
                  />
                  <ResetPasswordForm
                    member={member}
                    getFetchDataExtraActions={this.getFetchDataExtraActions}
                    disableButton={!member.canResetPassword}
                  />
                </div>
              </div>
            </div>
          </Modal.Body>
        )}
        <Modal.Footer>
          <Button white onClick={this.props.closeModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }
}

const mapStateToProps = () => {
  return {};
};

const mapDispatchToProps = {
  fetchStaffMember,
  promoteTeamOwner,
  demoteTeamOwner,
  activateUser,
  deactivateUser,
  removeMemberFromTeam,
  requiresPermission
};

export default withModal(
  MODALS.STAFF_MEMBER_DETAIL_MODAL,
  mapStateToProps,
  mapDispatchToProps
)(StaffMemberDetailModal);
